/**
 * CME MES (Micro E-mini S&P) candles drawn over the MNQ/NQ candles (issue #620).
 *
 * SOURCE. utt's own MES 2m candles (the engine streams the MES contract in
 * SYMBOLS): REST `GET /api/historical/{MES contract}?count=N` for history and
 * the engine's `market-data` broadcast (every streamed symbol goes to every
 * client) for the live tail. Same 2m bucket times as the chart's candles.
 *
 * RATIO ESTIMATOR. ES and NQ are different indices (~6.8k vs ~25k), so MES is
 * scaled multiplicatively, not shifted. On the native 2m grid a pair is a 2m
 * bucket closed on BOTH contracts, q = chart close / MES close (the forming
 * bucket is excluded on both sides). The ratio of the bar opening at t =
 * median of q over the last RATIO_WINDOW (30) pairs with open strictly before
 * t and no older than RATIO_MAX_AGE_MS (6h); at least RATIO_MIN_SAMPLES (3)
 * pairs or the bar gets no ratio and is not drawn. Same semantics as the Vest
 * gap (utils/vestCandles rollingGaps):
 * - Strictly earlier: a bar never divides out its own close, and the forming
 *   bar keeps the ratio it had once it closes (no repaint).
 * - Median: one bad print does not drag the scale.
 * - 6h cap: no Friday ratio after a weekend.
 * - Recomputed from the bars on hand on every update; never cached or stored.
 *
 * Each 2m MES bar is multiplied by its own ratio and folded to the chart bar
 * by utils/overlayCandles (points only at chart candle times, body contained).
 */
import { cmeClosedCloses, foldPlacedBars, median, type FoldedCandle, type PlacedBar } from './overlayCandles';
import { contractBase } from './symbolUtils';

export const RATIO_WINDOW = 30;
export const RATIO_MIN_SAMPLES = 3;
export const RATIO_MAX_AGE_MS = 6 * 60 * 60_000;

/**
 * Outline colours: pale indigo up (Tailwind indigo-300), pale amber down
 * (amber-300). Clear of the Vest overlay's pale green / red and of every
 * theme's candleUp/candleDown (src/config/themes.ts): the closest theme
 * colours are the cyans #9ccfd8 / #6fc3df and the orange #df740c, none blue
 * or yellow. Blue vs amber also reads for colour-blind users.
 */
export const MES_CANDLE_COLORS = { up: '#a5b4fc', down: '#fcd34d' } as const;

/** One MES 2m candle, CME prices. */
export interface MesBar {
  /** Open, ms. */
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
}

export interface ClosedPair {
  /** 2m bucket open, ms. */
  t: number;
  /** Chart close / MES close. */
  ratio: number;
}

export interface MesRatio {
  ratio: number;
  /** Pairs the median was taken over. */
  samples: number;
}

/** One overlay candle, on the chart's axis. */
export interface MesCandle {
  /** Chart bar open, seconds. */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  /** Ratio applied to the chart bar's last 2m bar. */
  ratio: number;
  /** 2m bars folded in. */
  bars: number;
}

const finite = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

const MONTH_CODES = 'FGHJKMNQUVXZ';

/**
 * Contract month of a futures symbol as a sortable number (year * 12 +
 * month), or null. `MESZ6.CME` -> 2026 Dec; one-digit years are taken in the
 * 2020s, two-digit years as 20yy.
 */
export function contractExpiry(symbol: string): { code: string; key: number } | null {
  const bare = symbol.trim().toUpperCase().replace(/\.[A-Z]+$/, '');
  const m = /([FGHJKMNQUVXZ])(\d{1,2})$/.exec(bare);
  if (!m || contractBase(symbol) === bare) return null;
  const year = m[2].length === 1 ? 2020 + Number(m[2]) : 2000 + Number(m[2]);
  return { code: `${m[1]}${year}`, key: year * 12 + MONTH_CODES.indexOf(m[1]) };
}

/**
 * The MES contract the engine streams (the same list the chart resolves its
 * own contract from). During a roll the list can hold two MES contracts:
 * MES and MNQ/NQ share quarterly months, so take the one in the chart's own
 * contract month; otherwise the latest expiry (the one being rolled into).
 */
export function resolveMesSymbol(activeSymbols: readonly string[], chartSymbol?: string | null): string | null {
  const mes = activeSymbols.filter((s) => contractBase(s) === 'MES');
  if (mes.length <= 1) return mes[0] ?? null;
  const want = chartSymbol ? contractExpiry(chartSymbol)?.code : undefined;
  if (want) {
    const same = mes.find((s) => contractExpiry(s)?.code === want);
    if (same) return same;
  }
  let best = mes[0];
  let bestKey = contractExpiry(best)?.key ?? -Infinity;
  for (const s of mes.slice(1)) {
    const k = contractExpiry(s)?.key ?? -Infinity;
    if (k > bestKey) {
      best = s;
      bestKey = k;
    }
  }
  return best;
}

/** Engine candles ({timestamp, open, high, low, close}) -> bars; rows missing a price are dropped. */
export function parseMesCandles(raw: unknown): MesBar[] {
  if (!Array.isArray(raw)) return [];
  const out: MesBar[] = [];
  for (const r of raw as Record<string, unknown>[]) {
    if (!r || typeof r !== 'object') continue;
    const { timestamp: t, open: o, high: h, low: l, close: c } = r;
    if (finite(t) && finite(o) && finite(h) && finite(l) && finite(c)) out.push({ t, o, h, l, c });
  }
  return out;
}

/** Merge bars by open time; null when nothing changed. */
export function mergeMesBars(existing: ReadonlyMap<number, MesBar>, incoming: readonly MesBar[]): Map<number, MesBar> | null {
  let next: Map<number, MesBar> | null = null;
  for (const b of incoming) {
    const cur = (next ?? existing).get(b.t);
    if (cur && cur.o === b.o && cur.h === b.h && cur.l === b.l && cur.c === b.c) continue;
    if (!next) next = new Map(existing);
    next.set(b.t, b);
  }
  return next;
}

/** MES closes per closed 2m bucket; the bucket holding `formingMs` is left out. */
export function mesCloses(bars: Iterable<MesBar>, formingMs: number | null): Map<number, number> {
  return cmeClosedCloses([...bars].map((b) => ({ timestamp: b.t, close: b.c })), formingMs);
}

/** Buckets closed on both, oldest first. Both inputs already exclude the forming bucket. */
export function closedPairs(chartCloses: ReadonlyMap<number, number>, mesCloseMap: ReadonlyMap<number, number>): ClosedPair[] {
  const out: ClosedPair[] = [];
  for (const [t, c] of chartCloses) {
    const m = mesCloseMap.get(t);
    if (!finite(c) || !finite(m) || !(c > 0) || !(m > 0)) continue;
    out.push({ t, ratio: c / m });
  }
  return out.sort((a, b) => a.t - b.t);
}

/** Ratio per bar open (ms) for every bar that has one (see header). */
export function rollingRatios(barTimesMs: Iterable<number>, pairs: readonly ClosedPair[]): Map<number, MesRatio> {
  const out = new Map<number, MesRatio>();
  if (pairs.length === 0) return out;
  const times = [...new Set(barTimesMs)].sort((a, b) => a - b);
  let k = 0; // pairs[0..k) open strictly before t
  for (const t of times) {
    while (k < pairs.length && pairs[k].t < t) k++;
    const window: number[] = [];
    for (let i = k - 1; i >= 0 && window.length < RATIO_WINDOW; i--) {
      if (pairs[i].t <= t - RATIO_MAX_AGE_MS) break;
      window.push(pairs[i].ratio);
    }
    if (window.length >= RATIO_MIN_SAMPLES) out.set(t, { ratio: median(window), samples: window.length });
  }
  return out;
}

/**
 * Overlay candles at the chart's candle times (seconds, ascending), on the
 * chart's axis. `timeframeMinutes` is the chart's.
 */
export function mesCandleData(
  chartTimesSec: readonly number[],
  bars: Iterable<MesBar>,
  ratios: ReadonlyMap<number, MesRatio>,
  timeframeMinutes: number,
): MesCandle[] {
  const placed: PlacedBar[] = [];
  for (const b of bars) {
    const r = ratios.get(b.t);
    placed.push(r ? { t: b.t, px: [b.o * r.ratio, b.h * r.ratio, b.l * r.ratio, b.c * r.ratio], adj: r.ratio } : { t: b.t, px: null, adj: NaN });
  }
  return foldPlacedBars(chartTimesSec, placed, timeframeMinutes).map(({ adj, ...c }: FoldedCandle) => ({ ...c, ratio: adj }));
}

/** The ratio applied to the newest drawn candle. */
export function latestRatio(data: readonly MesCandle[]): number | null {
  return data.length > 0 ? data[data.length - 1].ratio : null;
}

/** "ES x3.68": the scale applied to MES prices. */
export function ratioLabel(ratio: number | null): string {
  if (ratio === null || !Number.isFinite(ratio)) return 'ES …';
  return `ES x${ratio.toFixed(2)}`;
}
